import React, { useEffect, useState, useRef } from 'react';
import { MessageSquare, Save, AlignLeft, Type as TypeIcon, Trash, List, Plus, X, Eye, Link as LinkIcon, Bold, Italic, Strikethrough, Smartphone, Image as ImageIcon, Video, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../utils/api';
import axios from 'axios';
import Container from '../components/Container';
import FullScreenLoader from '../components/Loader';
import moment from 'moment';

const WABA_ID = '2228640350973996';
const APP_ID = '1261445062408947';
const WHATSAPP_TOKEN = 'EAAR7RxDZAOvMBP9UKBD5yxYgptfMXqphiPZCFrh2Huxb5AREvD35hudgZC4htDa4gsPbLZBQyCcpdqTwI4dAX2Izl3oPXpoZBBO4Vh4K833ZCWJlEv9kgUGEHyHddP1AzrZCgVByTGsGoiYZBCvH7YaSLzGwspw4BXLI8GhgDsiN7YnSGrPa9JdeqxVZANkL55wZDZD';

const WhatsappTemplate = () => {
  const [heading, setHeading] = useState('');
  const [type, setType] = useState('MARKETING');
  
  // New Fields
  const [headerFormat, setHeaderFormat] = useState('NONE');
  const [headerText, setHeaderText] = useState('');
  const [headerMedia, setHeaderMedia] = useState(null);
  const [message, setMessage] = useState('');
  const [footerText, setFooterText] = useState('');
  const [buttonText, setButtonText] = useState('');
  const [buttonUrl, setButtonUrl] = useState('');
  const [variableExamples, setVariableExamples] = useState([]);

  const [loading, setLoading] = useState(false);
  const [templateList, setTemplateList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  
  const [marketingWarning, setMarketingWarning] = useState(false);
  const [detectedWord, setDetectedWord] = useState('');
  
  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleteInput, setDeleteInput] = useState('');

  // View Modal State
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingTemplate, setViewingTemplate] = useState(null);
  const textAreaRef = useRef(null);

  // Extensive list of words that trigger WhatsApp's Marketing Category AI
  const marketingWords = [
    'offer', 'offers', 'discount', 'discounts', 'sale', 'sales', 'promo', 'promotion', 'promotional', 
    'free', 'buy', 'buy now', 'shop', 'shop now', 'click here', 'subscribe', 'coupon', 'voucher', 
    'cashback', 'deal', 'deals', 'win', 'prize', 'gift', 'giveaway', 'reward', 'rewards', 'bonus', 
    'limited time', 'hurry', 'exclusive', 'special price', 'get yours', 'claim', 'clearance', 
    'bargain', 'save', 'savings', 'jackpot', 'new arrival', 'pre-order', 'mega'
  ];

  useEffect(() => {
    getTemplate();
  }, []);

  // Validation for Create Template
  useEffect(() => {
    if (type === 'UTILITY') {
      const lowerMessage = message.toLowerCase();
      const foundWord = marketingWords.find(word => new RegExp(`\\b${word}\\b`, 'i').test(lowerMessage));
      if (foundWord) {
        setMarketingWarning(true);
        setDetectedWord(foundWord);
      } else {
        setMarketingWarning(false);
        setDetectedWord('');
      }
    } else {
      setMarketingWarning(false);
      setDetectedWord('');
    }
  }, [message, type]);



  const getTemplate = async () => {
    setLoading(true);
    try {
      if (WABA_ID === 'YOUR_WABA_ID' || WHATSAPP_TOKEN === 'YOUR_ACCESS_TOKEN') {
        toast.error("Please configure WABA_ID and WHATSAPP_TOKEN in the code or .env");
        setLoading(false);
        return;
      }

      const response = await axios.get(`https://graph.facebook.com/v19.0/${WABA_ID}/message_templates`, {
        headers: {
          'Authorization': `Bearer ${WHATSAPP_TOKEN}`
        }
      });

      if (response.status === 200 && response.data && response.data.data) {
        setTemplateList(response.data.data);
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error?.message || "Failed to fetch from WhatsApp API");
    } finally {
      setLoading(false);
    }
  };

  const getBodyText = (components) => {
    if (!components) return 'No Content';
    const bodyComponent = components.find(c => c.type === 'BODY');
    return bodyComponent ? bodyComponent.text : 'No Body Content';
  };

  const resetForm = () => {
    setHeading('');
    setType('MARKETING');
    setHeaderFormat('NONE');
    setHeaderText('');
    setHeaderMedia(null);
    setMessage('');
    setFooterText('');
    setButtonText('');
    setButtonUrl('');
    setVariableExamples([]);
    setShowForm(false);
  };

  const handleMessageChange = (e) => {
    const val = e.target.value;
    setMessage(val);
    
    const matches = val.match(/{{[0-9]+}}/g) || [];
    const uniqueVars = new Set(matches);
    const count = uniqueVars.size;
    
    if (variableExamples.length !== count) {
      const newExamples = Array(count).fill('');
      for(let i = 0; i < Math.min(count, variableExamples.length); i++) {
        newExamples[i] = variableExamples[i];
      }
      setVariableExamples(newExamples);
    }
  };

  const handleExampleChange = (index, val) => {
    const newEx = [...variableExamples];
    newEx[index] = val;
    setVariableExamples(newEx);
  };

  const insertFormatting = (formatChar) => {
    const textarea = textAreaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = message.substring(start, end);
    let newText = '';
    let newCursorPos = 0;

    if (start === end) {
      newText = message.substring(0, start) + formatChar + formatChar + message.substring(end);
      newCursorPos = start + formatChar.length;
    } else {
      newText = message.substring(0, start) + formatChar + selectedText + formatChar + message.substring(end);
      newCursorPos = end + (formatChar.length * 2);
    }
    
    handleMessageChange({ target: { value: newText } });

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleSaveTemplate = async () => {
    // Utility Marketing Block Prevention
    if (type === 'UTILITY' && marketingWarning) {
      toast.error(`UTILITY category mein "${detectedWord}" jaise words allowed nahi hain. Kripya inhe hatayein ya category MARKETING chunein.`);
      return;
    }

    if (!heading || !message) {
      toast.error("Please fill in Template Name and Message Content.");
      return;
    }

    if (headerFormat !== 'NONE' && headerFormat !== 'TEXT' && !headerMedia) {
      toast.error("Please select a media file for the header.");
      return;
    }

    if (variableExamples.some(ex => !ex.trim())) {
      toast.error("Please provide default/example values for all variables in the body.");
      return;
    }
    
    if ((buttonText && !buttonUrl) || (!buttonText && buttonUrl)) {
      toast.error("Please provide both Button Text and URL to add a link button.");
      return;
    }

    setLoading(true);
    try {
      const templateName = heading.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      const components = [];
      
      if (headerFormat === 'TEXT' && headerText.trim()) {
        components.push({
          type: "HEADER",
          format: "TEXT",
          text: headerText.trim()
        });
      } else if (headerFormat === 'IMAGE' || headerFormat === 'VIDEO' || headerFormat === 'DOCUMENT') {
        if (!headerMedia) {
          throw new Error(`Please attach a ${headerFormat.toLowerCase()} for the template header.`);
        }

        const sessionRes = await axios.post(
          `/fb-api/v19.0/${APP_ID}/uploads?file_length=${headerMedia.size}&file_type=${headerMedia.type}`,
          null,
          { headers: { 'Authorization': `Bearer ${WHATSAPP_TOKEN}` } }
        );
        
        const sessionId = sessionRes.data.id;
        
        const uploadRes = await fetch(
          `/fb-api/v19.0/${sessionId}`,
          { 
            method: 'POST',
            body: await headerMedia.arrayBuffer(),
            headers: { 
              'Authorization': `OAuth ${WHATSAPP_TOKEN}`,
              'file_offset': '0'
            } 
          }
        );
        
        if (!uploadRes.ok) {
           throw new Error("Failed to upload media via frontend proxy");
        }
        
        const uploadData = await uploadRes.json();
        const headerHandle = uploadData.h;

        if (!headerHandle) {
           throw new Error("No handle returned from Facebook");
        }

        components.push({
          type: "HEADER",
          format: headerFormat,
          example: {
            header_handle: [headerHandle]
          }
        });
      }

      const bodyComponent = {
        type: "BODY",
        text: message
      };
      
      if (variableExamples.length > 0) {
        bodyComponent.example = {
          body_text: [variableExamples]
        };
      }
      components.push(bodyComponent);

      if (footerText.trim()) {
        components.push({
          type: "FOOTER",
          text: footerText.trim()
        });
      }

      if (buttonText.trim() && buttonUrl.trim()) {
        let finalUrl = buttonUrl.trim();
        if (!/^https?:\/\//i.test(finalUrl)) {
          finalUrl = 'https://' + finalUrl;
        }

        try {
          new URL(finalUrl);
        } catch (e) {
          toast.error("Please enter a valid website URL. Example: https://google.com");
          setLoading(false);
          return;
        }

        let buttonPayload = {
          type: "URL",
          text: buttonText.trim(),
          url: finalUrl
        };

        if (finalUrl.includes('{{')) {
          let exampleUrl = finalUrl.replace(/{{[0-9]+}}/g, 'sample-path');
          buttonPayload.example = [exampleUrl];
        }

        components.push({
          type: "BUTTONS",
          buttons: [buttonPayload]
        });
      }

      const payload = {
        name: templateName,
        language: 'en',
        category: type,
        components: components
      };

      const response = await axios.post(
        `https://graph.facebook.com/v19.0/${WABA_ID}/message_templates`, 
        payload,
        {
          headers: { 
            'Authorization': `Bearer ${WHATSAPP_TOKEN}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success("WhatsApp Template created successfully!");
        resetForm();
        getTemplate();
      }
    } catch (err) {
      console.error(err);
      const errorObj = err?.response?.data?.error;
      let errorMsg = "Failed to create template on WhatsApp.";
      if (errorObj) {
        if (errorObj.error_user_msg) {
          errorMsg = `${errorObj.error_user_title ? errorObj.error_user_title + ': ' : ''}${errorObj.error_user_msg}`;
        } else if (errorObj.error_data?.details) {
          const details = typeof errorObj.error_data.details === 'string' ? errorObj.error_data.details : JSON.stringify(errorObj.error_data.details);
          errorMsg = `${errorObj.message} | Details: ${details}`;
        } else if (errorObj.message) {
          errorMsg = errorObj.message;
        }
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (name) => {
    setTemplateToDelete(name);
    setDeleteInput('');
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setTemplateToDelete(null);
    setDeleteInput('');
  };

  const openViewModal = (template) => {
    setViewingTemplate(template);
    setViewModalOpen(true);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setViewingTemplate(null);
  };

  const executeDelete = async () => {
    if (deleteInput !== templateToDelete) return;
    
    setLoading(true);
    closeDeleteModal();
    try {
      const response = await axios.delete(
        `https://graph.facebook.com/v19.0/${WABA_ID}/message_templates?name=${templateToDelete}`,
        {
          headers: { 'Authorization': `Bearer ${WHATSAPP_TOKEN}` }
        }
      );
      if (response.status === 200) {
        toast.success("Template Deleted Successfully from WhatsApp");
        getTemplate();
      }
    } catch (err) {
      const errorObj = err?.response?.data?.error;
      let errorMsg = "Failed to delete template from WhatsApp.";
      if (errorObj) {
        if (errorObj.error_user_msg) {
          errorMsg = `${errorObj.error_user_title ? errorObj.error_user_title + ': ' : ''}${errorObj.error_user_msg}`;
        } else if (errorObj.error_data?.details) {
          const details = typeof errorObj.error_data.details === 'string' ? errorObj.error_data.details : JSON.stringify(errorObj.error_data.details);
          errorMsg = `${errorObj.message} | Details: ${details}`;
        } else if (errorObj.message) {
          errorMsg = errorObj.message;
        }
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container 
      title={'WhatsApp Templates'} 
      onClick={getTemplate}
      rightSection={
        <button 
          onClick={() => (showForm ? resetForm() : setShowForm(true))} 
          style={showForm ? styles.btnClose : styles.btnAdd}
        >
          {showForm ? <><X size={16} /> Close</> : <><Plus size={16} /> Add Template</>}
        </button>
      }
    >
      {loading && <FullScreenLoader />}

      <div style={styles.mainWrapper}>
        {showForm && (
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '20px', width: '100%' }}>
            {/* Form Column */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <MessageSquare size={18} color="#25D366" />
                <span style={styles.cardTitle}>New WhatsApp Template Builder</span>
              </div>

              <div style={styles.formGrid}>
                {/* Left Column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}><TypeIcon size={14} /> Template Name</label>
                    <input
                      type="text"
                      style={styles.textInput}
                      value={heading}
                      onChange={(e) => setHeading(e.target.value)}
                      placeholder="e.g. seasonal_promotion"
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}><List size={14} /> Category</label>
                    <select
                      style={styles.textInput}
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                    >
                      <option value="MARKETING">Marketing</option>
                      <option value="UTILITY">Utility</option>
                    </select>
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}><TypeIcon size={14} /> Header Format</label>
                    <select
                      style={styles.textInput}
                      value={headerFormat}
                      onChange={(e) => {
                        setHeaderFormat(e.target.value);
                        setHeaderText('');
                        setHeaderMedia(null);
                      }}
                    >
                      <option value="NONE">None</option>
                      <option value="TEXT">Text</option>
                      <option value="IMAGE">Image</option>
                      <option value="VIDEO">Video</option>
                      <option value="DOCUMENT">Document</option>
                    </select>
                  </div>

                  {headerFormat === 'TEXT' && (
                    <div style={styles.inputGroup}>
                      <label style={styles.label}><TypeIcon size={14} /> Header Text <span style={styles.optional}>(Optional)</span></label>
                      <input
                        type="text"
                        style={styles.textInput}
                        value={headerText}
                        onChange={(e) => setHeaderText(e.target.value)}
                        maxLength={60}
                        placeholder="Large text at the top (Max 60 chars)"
                      />
                    </div>
                  )}

                  {(headerFormat === 'IMAGE' || headerFormat === 'VIDEO' || headerFormat === 'DOCUMENT') && (
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Upload {headerFormat.charAt(0) + headerFormat.slice(1).toLowerCase()} Example</label>
                      <input
                        type="file"
                        accept={headerFormat === 'IMAGE' ? "image/*" : headerFormat === 'VIDEO' ? "video/*" : ".pdf,.doc,.docx"}
                        style={styles.textInput}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file && file.size > 2097152) {
                            toast.error("File size exceeds the 2MB limit.");
                            e.target.value = null;
                            setHeaderMedia(null);
                          } else {
                            setHeaderMedia(file);
                          }
                        }}
                      />
                      <p style={{fontSize: '11px', color: '#64748b', marginTop: '4px'}}>An example media is required to create a media template.</p>
                    </div>
                  )}

                  <div style={styles.highlightBox}>
                    <label style={styles.label}><LinkIcon size={14} /> Call-to-Action Button <span style={styles.optional}>(Optional)</span></label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
                      <input 
                        type="text" 
                        style={styles.textInput} 
                        value={buttonText} 
                        onChange={e => setButtonText(e.target.value)} 
                        maxLength={25}
                        placeholder="Button Text (e.g. Visit Website)" 
                      />
                      <input 
                        type="url" 
                        style={styles.textInput} 
                        value={buttonUrl} 
                        onChange={e => setButtonUrl(e.target.value)} 
                        placeholder="URL (e.g. https://google.com)" 
                      />
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <div style={styles.inputGroup}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <label style={{ ...styles.label, marginBottom: 0 }}><AlignLeft size={14} /> Message Body</label>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <button type="button" style={styles.formatBtn} onClick={() => insertFormatting('*')} title="Bold"><Bold size={14} /></button>
                        <button type="button" style={styles.formatBtn} onClick={() => insertFormatting('_')} title="Italic"><Italic size={14} /></button>
                        <button type="button" style={styles.formatBtn} onClick={() => insertFormatting('~')} title="Strikethrough"><Strikethrough size={14} /></button>
                        <button 
                          type="button" 
                          style={styles.btnAddVar} 
                          onClick={() => {
                            const count = (message.match(/{{[0-9]+}}/g) || []).length;
                            handleMessageChange({target: {value: message + ` {{${count + 1}}}`}});
                          }}
                        >
                          + Add Variable
                        </button>
                      </div>
                    </div>
                    <textarea
                      ref={textAreaRef}
                      style={{ ...styles.textArea, height: '150px' }}
                      value={message}
                      onChange={handleMessageChange}
                      placeholder="Enter template body content..."
                    />
                    {marketingWarning && (
                      <div style={{ backgroundColor: '#fee2e2', color: '#991b1b', padding: '10px', borderRadius: '5px', fontSize: '12px', marginTop: '8px', border: '1px solid #f87171' }}>
                        <strong>⚠️ Action Required:</strong> Aapne <strong>UTILITY</strong> category select ki hai, par aapke message mein "<strong>{detectedWord}</strong>" word hai jise WhatsApp Marketing maanta hai. 
                        Template save karne ke liye kripya is word ko message se hatayein ya category ko 'MARKETING' karein.
                      </div>
                    )}
                  </div>

                  {variableExamples.length > 0 && (
                    <div style={styles.highlightBox}>
                      <label style={styles.label}>Default Values for Variables</label>
                      <p style={{fontSize: '11px', color: '#64748b', margin: '0 0 10px 0'}}>WhatsApp needs an example value for each variable to approve the template.</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {variableExamples.map((ex, idx) => (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#1e40af', width: '30px' }}>{`{{${idx+1}}}`}</span>
                            <input 
                              type="text" 
                              style={{...styles.textInput, padding: '8px', flex: 1}} 
                              value={ex} 
                              onChange={e => handleExampleChange(idx, e.target.value)} 
                              placeholder={`e.g. John Doe`} 
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={styles.inputGroup}>
                    <label style={styles.label}><TypeIcon size={14} /> Footer Text <span style={styles.optional}>(Optional)</span></label>
                    <input
                      type="text"
                      style={styles.textInput}
                      value={footerText}
                      onChange={(e) => setFooterText(e.target.value)}
                      maxLength={60}
                      placeholder="Small text at the bottom..."
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', borderTop: '1px solid #e2e8f0', paddingTop: '15px' }}>
                <button
                  style={(heading && message && !marketingWarning) ? styles.btnSaveActive : styles.btnSaveDisabled}
                  disabled={!heading || !message || (type === 'UTILITY' && marketingWarning)}
                  onClick={handleSaveTemplate}
                >
                  <Save size={18} /> Submit to WhatsApp
                </button>
              </div>
            </div>
            
            {/* Preview Column */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <Smartphone size={18} color="#25D366" />
                <span style={styles.cardTitle}>Preview</span>
              </div>
              <div style={styles.phonePreview}>
                <div style={styles.chatBubble}>
                  {headerFormat === 'TEXT' && headerText && (
                    <div style={styles.previewHeader}>{headerText}</div>
                  )}
                  {headerFormat === 'IMAGE' && (
                    <div style={styles.previewMedia}>
                      {headerMedia ? <img src={URL.createObjectURL(headerMedia)} alt="Preview" style={styles.previewImg} /> : <div style={styles.mediaPlaceholder}><ImageIcon size={24} color="#94a3b8" /></div>}
                    </div>
                  )}
                  {headerFormat === 'VIDEO' && (
                    <div style={styles.previewMedia}>
                      {headerMedia ? <video src={URL.createObjectURL(headerMedia)} style={styles.previewImg} controls /> : <div style={styles.mediaPlaceholder}><Video size={24} color="#94a3b8" /></div>}
                    </div>
                  )}
                  {headerFormat === 'DOCUMENT' && (
                    <div style={styles.previewMedia}>
                       <div style={styles.docPlaceholder}>
                          <FileText size={24} color="#ef4444" />
                          <span style={{fontSize: '12px', marginTop: '5px', color: '#64748b'}}>{headerMedia ? headerMedia.name : 'Document Preview'}</span>
                       </div>
                    </div>
                  )}
                  
                  <div style={styles.previewBody}>
                    {message.split('\n').map((line, i) => (
                      <React.Fragment key={i}>
                        {line.replace(/{{(\d+)}}/g, (match, num) => {
                           const idx = parseInt(num) - 1;
                           return variableExamples[idx] ? variableExamples[idx] : `[${match}]`;
                        })}
                        <br />
                      </React.Fragment>
                    ))}
                  </div>
                  
                  {footerText && <div style={styles.previewFooter}>{footerText}</div>}
                </div>
                
                {(buttonText || buttonUrl) && (
                  <div style={styles.previewButton}>
                     <LinkIcon size={14} color="#0ea5e9" /> {buttonText || 'Button'}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Listing Section */}
        <div style={styles.listSection}>
          <div style={styles.listHeaderIcon}>
            <List size={18} color="#25D366" />
            <span style={styles.cardTitle}>WhatsApp Server Templates ({templateList.length})</span>
          </div>
          
          <div style={tableWrapper}>
            <table style={tableStyle}>
              <thead>
                <tr style={thRow}>
                  <th style={thStyle}>S No.</th>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Category</th>
                  <th style={thStyle}>Language</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Content Preview</th>
                  <th style={thStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {templateList.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>
                      No templates found. Make sure API keys are correct.
                    </td>
                  </tr>
                ) : (
                  templateList.map((item, index) => (
                    <tr key={index} style={trStyle}>
                      <td style={tdStyle}>{index + 1}</td>
                      <td style={tdStyle}><strong>{item.name}</strong></td>
                      <td style={tdStyle}>
                        <span style={styles.categoryBadge(item.category)}>
                          {item.category || 'N/A'}
                        </span>
                      </td>
                      <td style={tdStyle}>{item.language}</td>
                      <td style={tdStyle}>
                         <span style={styles.statusBadge(item.status)}>
                          {item.status}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <div style={styles.msgColumn}>
                          {item.components.find(c => c.type === 'HEADER' && c.format !== 'TEXT') && (
                             <strong style={{color: '#3b82f6'}}>
                               [{item.components.find(c => c.type === 'HEADER').format}] 
                             </strong>
                          )} {getBodyText(item.components)}
                        </div>
                      </td>
                       <td style={tdStyle}>
                         <div style={{ display: 'flex', gap: '8px' }}>
                           <button style={styles.viewBtn} onClick={() => openViewModal(item)} title="View Template"><Eye size={15} /></button>
                           <button style={styles.deleteBtn} onClick={() => openDeleteModal(item.name)} title="Delete from WhatsApp"><Trash size={16} /></button>
                         </div>
                       </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, color: '#1e293b', fontSize: '18px' }}>Confirm Deletion</h3>
              <button onClick={closeDeleteModal} style={styles.closeIconBtn}><X size={20} /></button>
            </div>
            <div style={styles.modalBody}>
              <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '15px', lineHeight: '1.5' }}>
                This action cannot be undone. To confirm, please type the template name:
                <strong style={{ display: 'block', color: '#0f172a', marginTop: '5px', fontSize: '15px' }}>{templateToDelete}</strong>
              </p>
              <input 
                type="text" 
                style={styles.textInput} 
                value={deleteInput}
                onChange={(e) => setDeleteInput(e.target.value)}
                placeholder="Type template name here"
              />
            </div>
            <div style={styles.modalFooter}>
              <button onClick={closeDeleteModal} style={styles.btnCancel}>Cancel</button>
              <button 
                onClick={executeDelete} 
                style={deleteInput === templateToDelete ? styles.btnDeleteActive : styles.btnDeleteDisabled}
                disabled={deleteInput !== templateToDelete}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Template Modal */}
      {viewModalOpen && viewingTemplate && (() => {
        const headerComp = viewingTemplate.components?.find(c => c.type === 'HEADER');
        const bodyComp = viewingTemplate.components?.find(c => c.type === 'BODY');
        const footerComp = viewingTemplate.components?.find(c => c.type === 'FOOTER');
        const buttonsComp = viewingTemplate.components?.find(c => c.type === 'BUTTONS');
        const bodyText = bodyComp?.text || '';
        const exampleValues = bodyComp?.example?.body_text?.[0] || [];
        const buttonsList = buttonsComp?.buttons || [];

        return (
          <div style={styles.modalOverlay}>
            <div style={styles.viewModalContent}>
              <div style={styles.modalHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', backgroundColor: '#e0f2fe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Eye size={18} color="#0284c7" />
                  </div>
                  <h3 style={{ margin: 0, color: '#0f172a', fontSize: '18px', fontWeight: '700' }}>View Template</h3>
                  <span style={styles.viewTemplateNameBadge}>{viewingTemplate.name}</span>
                  <span style={styles.statusBadge(viewingTemplate.status)}>{viewingTemplate.status}</span>
                  <span style={styles.categoryBadge(viewingTemplate.category)}>{viewingTemplate.category}</span>
                </div>
                <button onClick={closeViewModal} style={styles.closeIconBtn}><X size={20} /></button>
              </div>

              <div style={styles.viewModalBody}>
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '24px' }}>
                  {/* Left: Template Details */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Meta Overview Cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                      <div style={styles.metaCard}>
                        <span style={styles.metaLabel}>Category</span>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{viewingTemplate.category || 'N/A'}</span>
                      </div>
                      <div style={styles.metaCard}>
                        <span style={styles.metaLabel}>Language</span>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{viewingTemplate.language || 'en'}</span>
                      </div>
                      <div style={styles.metaCard}>
                        <span style={styles.metaLabel}>Status</span>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: viewingTemplate.status === 'APPROVED' ? '#166534' : viewingTemplate.status === 'REJECTED' ? '#991b1b' : '#854d0e' }}>
                          {viewingTemplate.status || 'N/A'}
                        </span>
                      </div>
                    </div>

                    {/* Header Details */}
                    {headerComp && (
                      <div style={styles.detailBox}>
                        <label style={styles.viewSectionLabel}>
                          <TypeIcon size={14} color="#64748b" /> Header ({headerComp.format})
                        </label>
                        {headerComp.format === 'TEXT' ? (
                          <div style={styles.detailText}>{headerComp.text}</div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '13px', marginTop: '6px' }}>
                            {headerComp.format === 'IMAGE' && <ImageIcon size={18} color="#3b82f6" />}
                            {headerComp.format === 'VIDEO' && <Video size={18} color="#8b5cf6" />}
                            {headerComp.format === 'DOCUMENT' && <FileText size={18} color="#ef4444" />}
                            <span>Media Format: <strong>{headerComp.format}</strong></span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Body Details */}
                    <div style={styles.detailBox}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <label style={styles.viewSectionLabel}>
                          <AlignLeft size={14} color="#64748b" /> Message Body
                        </label>
                        {exampleValues.length > 0 && (
                          <span style={{ fontSize: '11px', color: '#4f46e5', fontWeight: '600', backgroundColor: '#eef2ff', padding: '2px 8px', borderRadius: '4px' }}>
                            {exampleValues.length} Variable{exampleValues.length > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <div style={styles.bodyContentBox}>
                        {bodyText || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No message body</span>}
                      </div>

                      {/* Variable examples if present */}
                      {exampleValues.length > 0 && (
                        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #e2e8f0' }}>
                          <span style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>Sample Variable Values:</span>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {exampleValues.map((val, idx) => (
                              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                                <span style={{ fontFamily: 'monospace', fontWeight: '700', color: '#4f46e5', backgroundColor: '#e0e7ff', padding: '2px 6px', borderRadius: '4px' }}>
                                  {`{{${idx + 1}}}`}
                                </span>
                                <span style={{ color: '#334155' }}>"{val}"</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Footer Details */}
                    {footerComp?.text && (
                      <div style={styles.detailBox}>
                        <label style={styles.viewSectionLabel}>
                          <TypeIcon size={14} color="#64748b" /> Footer Text
                        </label>
                        <div style={styles.detailText}>{footerComp.text}</div>
                      </div>
                    )}

                    {/* Buttons Details */}
                    {buttonsList.length > 0 && (
                      <div style={styles.detailBox}>
                        <label style={styles.viewSectionLabel}>
                          <LinkIcon size={14} color="#64748b" /> Buttons & Actions
                        </label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
                          {buttonsList.map((btn, idx) => (
                            <div key={idx} style={styles.buttonItemBox}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <LinkIcon size={13} color="#0284c7" />
                                <strong style={{ fontSize: '13px', color: '#0f172a' }}>{btn.text}</strong>
                                <span style={{ fontSize: '10px', backgroundColor: '#e2e8f0', color: '#475569', padding: '1px 6px', borderRadius: '4px', textTransform: 'uppercase', fontWeight: '700' }}>
                                  {btn.type}
                                </span>
                              </div>
                              {btn.url && (
                                <a href={btn.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#2563eb', wordBreak: 'break-all', textDecoration: 'none' }}>
                                  {btn.url}
                                </a>
                              )}
                              {btn.phone_number && (
                                <span style={{ fontSize: '12px', color: '#475569' }}>Tel: {btn.phone_number}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right: Live WhatsApp Preview */}
                  <div>
                    <label style={{ ...styles.viewSectionLabel, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Smartphone size={14} color="#25D366" /> Live Preview
                    </label>
                    <div style={styles.phonePreview}>
                      <div style={styles.chatBubble}>
                        {headerComp && (
                          <>
                            {headerComp.format === 'TEXT' && (
                              <div style={styles.previewHeader}>{headerComp.text}</div>
                            )}
                            {headerComp.format === 'IMAGE' && (
                              <div style={styles.previewMedia}>
                                <div style={styles.mediaPlaceholder}><ImageIcon size={24} color="#94a3b8" /></div>
                              </div>
                            )}
                            {headerComp.format === 'VIDEO' && (
                              <div style={styles.previewMedia}>
                                <div style={styles.mediaPlaceholder}><Video size={24} color="#94a3b8" /></div>
                              </div>
                            )}
                            {headerComp.format === 'DOCUMENT' && (
                              <div style={styles.previewMedia}>
                                <div style={styles.docPlaceholder}>
                                  <FileText size={24} color="#ef4444" />
                                  <span style={{ fontSize: '12px', marginTop: '5px', color: '#64748b' }}>Document</span>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                        <div style={styles.previewBody}>
                          {bodyText ? bodyText.split('\n').map((line, i) => (
                            <React.Fragment key={i}>
                              {line.replace(/{{(\d+)}}/g, (match, num) => {
                                const idx = parseInt(num, 10) - 1;
                                return exampleValues[idx] ? exampleValues[idx] : `[${match}]`;
                              })}
                              <br />
                            </React.Fragment>
                          )) : <span style={{ color: '#94a3b8' }}>Empty Body</span>}
                        </div>
                        {footerComp?.text && (
                          <div style={styles.previewFooter}>{footerComp.text}</div>
                        )}
                      </div>

                      {/* Preview Buttons */}
                      {buttonsList.map((btn, idx) => (
                        <div key={idx} style={styles.previewButton}>
                          <LinkIcon size={14} color="#0ea5e9" /> {btn.text || 'Button'}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button onClick={closeViewModal} style={styles.btnCloseView}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}
    </Container>
  );
};

const styles = {
  mainWrapper: { display: 'flex', flexDirection: 'column', gap: '25px', marginTop: '10px' },
  btnAdd: { backgroundColor: '#25D366', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' },
  btnAddVar: { backgroundColor: '#f1f5f9', color: '#4f46e5', border: '1px solid #e2e8f0', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  formatBtn: { backgroundColor: '#f8fafc', color: '#333', border: '1px solid #e2e8f0', padding: '4px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  btnClose: { backgroundColor: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', padding: '8px 16px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' },
  formSection: { width: '100%' },
  card: { backgroundColor: 'white', padding: '20px', borderRadius: '15px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' },
  cardHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' },
  cardTitle: { fontWeight: '700', fontSize: '16px', color: '#1e293b' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' },
  inputGroup: { marginBottom: '10px' },
  label: { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '8px' },
  optional: { fontSize: '11px', color: '#94a3b8', fontWeight: 'normal' },
  textInput: { width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' },
  textArea: { width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc', resize: 'vertical' },
  highlightBox: { padding: '15px', backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px dashed #cbd5e1', marginBottom: '10px' },
  btnSaveActive: { backgroundColor: '#25D366', color: 'white', padding: '10px 25px', borderRadius: '10px', border: 'none', fontWeight: '700', cursor: 'pointer', display: 'flex', gap: '8px' },
  btnSaveDisabled: { backgroundColor: '#e2e8f0', color: '#94a3b8', padding: '10px 25px', borderRadius: '10px', border: 'none', fontWeight: '700', cursor: 'not-allowed', display: 'flex', gap: '8px' },
  listSection: { width: '100%' },
  listHeaderIcon: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' },
  msgColumn: { maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  viewBtn: { padding: '6px 10px', backgroundColor: '#e0f2fe', color: '#0284c7', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  deleteBtn: { padding: '6px 10px', backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' },
  categoryBadge: (type) => ({
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: type === 'MARKETING' ? '#dbeafe' : type === 'UTILITY' ? '#fef3c7' : '#e0e7ff',
    color: type === 'MARKETING' ? '#1e40af' : type === 'UTILITY' ? '#92400e' : '#3730a3',
  }),
  statusBadge: (status) => ({
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: status === 'APPROVED' ? '#dcfce7' : status === 'REJECTED' ? '#fee2e2' : '#fef9c3',
    color: status === 'APPROVED' ? '#166534' : status === 'REJECTED' ? '#991b1b' : '#854d0e',
  }),
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalContent: { backgroundColor: '#fff', borderRadius: '12px', width: '400px', maxWidth: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
  modalHeader: { padding: '20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  closeIconBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex' },
  modalBody: { padding: '20px',paddingTop:'0px' },
  modalFooter: { padding: '15px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px', backgroundColor: '#f8fafc', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' },
  btnCancel: { padding: '8px 16px', backgroundColor: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#475569', cursor: 'pointer', fontWeight: '600' },
  btnDeleteActive: { padding: '8px 16px', backgroundColor: '#ef4444', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer', fontWeight: '600' },
  btnDeleteDisabled: { padding: '8px 16px', backgroundColor: '#fca5a5', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'not-allowed', fontWeight: '600' },
  viewModalContent: { backgroundColor: '#fff', borderRadius: '16px', width: '920px', maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.15)' },
  viewModalBody: { padding: '20px', overflowY: 'auto', flex: 1 },
  viewTemplateNameBadge: { backgroundColor: '#e0f2fe', color: '#0369a1', padding: '3px 10px', borderRadius: '20px', fontSize: '13px', fontWeight: '700', fontFamily: 'monospace' },
  metaCard: { backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '4px' },
  metaLabel: { fontSize: '11px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' },
  detailBox: { backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px' },
  viewSectionLabel: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' },
  detailText: { fontSize: '13px', color: '#1e293b', marginTop: '6px', lineHeight: '1.5' },
  bodyContentBox: { backgroundColor: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '12px', fontSize: '13px', color: '#0f172a', lineHeight: '1.6', whiteSpace: 'pre-wrap', marginTop: '6px', maxHeight: '200px', overflowY: 'auto' },
  buttonItemBox: { backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: '4px' },
  btnCloseView: { padding: '9px 22px', backgroundColor: '#334155', border: 'none', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontWeight: '600', fontSize: '13px' },
  phonePreview: {
    backgroundColor: '#e5ddd5',
    backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")',
    backgroundSize: 'cover',
    borderRadius: '15px',
    padding: '20px',
    minHeight: '400px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.1)',
    position: 'relative'
  },
  chatBubble: {
    backgroundColor: '#fff',
    borderRadius: '8px 8px 8px 0',
    padding: '8px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    maxWidth: '100%',
    marginBottom: '4px'
  },
  previewHeader: {
    fontWeight: 'bold',
    fontSize: '15px',
    color: '#000',
    marginBottom: '5px'
  },
  previewMedia: {
    marginBottom: '8px',
    borderRadius: '6px',
    overflow: 'hidden',
    backgroundColor: '#f1f5f9',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  previewImg: {
    width: '100%',
    maxHeight: '180px',
    objectFit: 'cover'
  },
  mediaPlaceholder: {
    height: '140px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  docPlaceholder: {
    height: '80px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    width: '100%',
    border: '1px solid #e2e8f0',
    borderRadius: '6px'
  },
  previewBody: {
    fontSize: '14px',
    color: '#111',
    lineHeight: '1.4',
    whiteSpace: 'pre-wrap'
  },
  previewFooter: {
    fontSize: '12px',
    color: '#64748b',
    marginTop: '6px',
    paddingTop: '6px'
  },
  previewButton: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    color: '#0ea5e9',
    fontWeight: 'bold',
    fontSize: '14px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
  }
};

const tableWrapper = { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' };
const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const thRow = { backgroundColor: '#f8fafc' };
const thStyle = { padding: '15px', textAlign: 'left', borderBottom: '2px solid #f1f5f9', color: '#64748b', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase' };
const tdStyle = { padding: '15px', borderBottom: '1px solid #f1f5f9', fontSize: '14px' };
const trStyle = { transition: '0.2s' };

export default WhatsappTemplate;